import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { swapAPI, assetAPI, walletAPI } from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowUpDown, Info, Clock, RotateCcw } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

const Exchange = () => {
  const [assets, setAssets] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [swapHistory, setSwapHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Swap form state
  const [fromAsset, setFromAsset] = useState('BTC');
  const [toAsset, setToAsset] = useState('USDT');
  const [fromAmount, setFromAmount] = useState('0');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [toAmount, setToAmount] = useState('0');
  const [networkFee, setNetworkFee] = useState('~$2.50');
  const [estimatedTime, setEstimatedTime] = useState('~2 minutes');

  const SWAP_FEE_RATE = 0.001; // 0.1% similar to common CEX spot fee tiers
  const NETWORK_FEE_ESTIMATES_USD = {
    BTC: 1.2,
    ETH: 0.75,
    USDT: 0.35,
    BNB: 0.2,
    MATIC: 0.08,
  };


  useEffect(() => {
    fetchData();
  }, []);

  // Update fromAmount when wallets or fromAsset changes
  useEffect(() => {
    const wallet = wallets.find(
      (w) => (w.asset_symbol || '').toUpperCase() === fromAsset.toUpperCase()
    );
    setFromAmount(wallet ? wallet.available_balance.toString() : '0');
  }, [wallets, fromAsset]);

  useEffect(() => {
    if (fromAsset && toAsset && fromAmount && parseFloat(fromAmount) > 0) {
      calculateExchangeRate();
    } else {
      setToAmount('0');
      setExchangeRate(null);
    }
  }, [fromAsset, toAsset, fromAmount]);

  useEffect(() => {
    if (fromAsset === toAsset) {
      const fallbackToAsset = assets.find(
        (asset) => asset.type === 'crypto' && asset.symbol !== fromAsset
      )?.symbol;

      if (fallbackToAsset) {
        setToAsset(fallbackToAsset);
      }
    }
  }, [assets, fromAsset, toAsset]);

  const fetchData = async () => {
    try {
      const [assetsRes, walletsRes, historyRes] = await Promise.all([
        assetAPI.getAll(),
        walletAPI.getAll(),
        swapAPI.getHistory(),
      ]);

      setAssets(assetsRes.data.filter((a) => a.type === 'crypto'));
      setWallets(walletsRes.data);
      setSwapHistory(historyRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load exchange data');
    } finally {
      setLoading(false);
    }
  };

  const calculateExchangeRate = async () => {
    try {
      const fromPrice =
        assets.find((a) => a.symbol === fromAsset)?.current_price || 0;
      const toPrice =
        assets.find((a) => a.symbol === toAsset)?.current_price || 1;

      if (fromPrice > 0 && toPrice > 0) {
        const rate = fromPrice / toPrice;
        setExchangeRate(rate);

        // Calculate to amount (with 0.1% swap fee)
        const amount = parseFloat(fromAmount);
        const calculatedAmount = (amount * rate * (1 - SWAP_FEE_RATE)).toFixed(2);
        setToAmount(calculatedAmount);

        // Estimate network fee by source chain/asset (flat estimate, closer to CEX UX)
        const feeValue = (
          NETWORK_FEE_ESTIMATES_USD[fromAsset] ?? NETWORK_FEE_ESTIMATES_USD.USDT
        ).toFixed(2);
        setNetworkFee(`~$${feeValue}`);
      }
    } catch (error) {
      console.error('Failed to calculate exchange rate:', error);
    }
  };

  const getBalance = (symbol) => {
    const wallet = wallets.find(
      (w) => (w.asset_symbol || '').toUpperCase() === (symbol || '').toUpperCase()
    );
    return wallet?.available_balance || 0;
  };

  const handleMax = () => {
    const balance = getBalance(fromAsset);
    setFromAmount(balance ? balance.toString() : '0');
  };

  const handleSwapAssets = () => {
    const temp = fromAsset;
    setFromAsset(toAsset);
    setToAsset(temp);
    setFromAmount('0');
  };

  const handleExchange = async (e) => {
    e.preventDefault();

    if (!fromAsset || !toAsset || !fromAmount || parseFloat(fromAmount) <= 0) {
      toast.error('Please fill in all fields');
      return;
    }

    const balance = getBalance(fromAsset);
    if (parseFloat(fromAmount) > balance) {
      toast.error('Insufficient balance');
      return;
    }

    try {
      const response = await swapAPI.request({
        from_asset: fromAsset,
        to_asset: toAsset,
        from_amount: parseFloat(fromAmount),
      });

      toast.success(
        `Exchange successful! You'll receive ~${response.data.estimated_amount} ${toAsset}`
      );
      setFromAmount('0');
      setToAmount('0');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Exchange failed');
    }
  };

  const getAssetIcon = (symbol) => {
    const asset = assets.find((a) => a.symbol === symbol);
    if (!asset) return symbol.charAt(0);

    // Color mapping for common assets
    const colors = {
      BTC: '#F7931A',
      ETH: '#627EEA',
      USDT: '#26A17B',
      BNB: '#F3BA2F',
      MATIC: '#8247E5',
    };

    return {
      letter: symbol.charAt(0),
      color: colors[symbol] || '#21bcfe',
    };
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60)
      return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24)
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  const cryptoAssets = assets.filter((a) => a.type === 'crypto');
  const toAssetOptions = cryptoAssets.filter((asset) => asset.symbol !== fromAsset);
  const fromIcon = getAssetIcon(fromAsset);
  const toIcon = getAssetIcon(toAsset);

  return (
    <div
      className="space-y-6 pl-10 pr-6 py-10 animate-fadeIn bg-[#F9F9F9]"
      data-testid="exchange-page"
    >
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Exchange
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Swap cryptocurrencies instantly
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Swap Tokens */}
        <div className="lg:col-span-8">
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Swap Tokens
            </h2>

            <form onSubmit={handleExchange} className="space-y-6">
              {/* From Section */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  From
                </Label>
                <div className="relative">
                  <div className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                    <div className="flex items-center space-x-3 flex-1">
                      <div
                        className="w-10 h-10 p-2 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: fromIcon.color }}
                      >
                        {fromIcon.letter}
                      </div>
                      <Select value={fromAsset} onValueChange={setFromAsset}>
                        <SelectTrigger className="border-0 p-0 h-auto w-auto focus:ring-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {cryptoAssets.map((asset) => (
                            <SelectItem key={asset.symbol} value={asset.symbol}>
                              {asset.name} ({asset.symbol})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col items-end">
                      <Input
                        type="number"
                        step="0.00000001"
                        value={fromAmount}
                        onChange={(e) => setFromAmount(e.target.value)}
                        className="border-0 px-3 py-2 text-right text-lg font-semibold focus-visible:ring-0 bg-transparent"
                        placeholder="0.00"
                        required
                      />
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-blue-600 hover:text-blue-700 text-xs font-medium"
                        onClick={handleMax}
                      >
                        MAX
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-1">
                    Balance: {getBalance(fromAsset).toFixed(4)} {fromAsset}
                  </p>
                </div>
              </div>

              {/* Swap Button */}
              <div className="flex justify-center -my-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="rounded-xl w-12 h-12 border-2 bg-[#062136] text-white"
                  onClick={handleSwapAssets}
                >
                  <ArrowUpDown className="w-5 h-5" />
                </Button>
              </div>

              {/* To Section */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  To
                </Label>
                <div className="relative">
                  <div className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                    <div className="flex items-center space-x-3 flex-1">
                      <div
                        className="w-10 h-10 p-2 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: toIcon.color }}
                      >
                        {toIcon.letter}
                      </div>
                      <Select value={toAsset} onValueChange={setToAsset}>
                        <SelectTrigger className="border-0 p-0 h-auto w-auto focus:ring-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {toAssetOptions.map((asset) => (
                            <SelectItem key={asset.symbol} value={asset.symbol}>
                              {asset.name} ({asset.symbol})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {parseFloat(toAmount).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-1">
                    Balance: {getBalance(toAsset).toFixed(2)} {toAsset}
                  </p>
                </div>
              </div>

              {/* Exchange Details */}
              <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Exchange Rate
                    </span>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {exchangeRate
                      ? `1 ${fromAsset} = ${exchangeRate.toLocaleString(
                        'en-US',
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )} ${toAsset}`
                      : '--'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Network Fee
                    </span>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {networkFee}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Estimated Time
                    </span>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {estimatedTime}
                  </span>
                </div>
              </div>

              {/* Exchange Now Button */}
              <Button
                type="submit"
                className="w-full bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold py-6 text-lg"
              >
                Exchange Now
              </Button>
            </form>
          </Card>
        </div>

        {/* Right Column - Recent Exchanges */}
        <div className="lg:col-span-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Recent Exchanges
              </h2>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={fetchData}
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-3">
              {swapHistory.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No recent exchanges
                  </p>
                </div>
              ) : (
                swapHistory.slice(0, 10).map((swap) => {
                  const fromIcon = getAssetIcon(swap.from_asset);
                  const toIcon = getAssetIcon(swap.to_asset);

                  return (
                    <div
                      key={swap.id}
                      className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div
                          className="w-8 h-8 p-1 rounded-full flex items-center justify-center text-white text-xs font-bold"
                          style={{ backgroundColor: fromIcon.color }}
                        >
                          {fromIcon.letter}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {swap.from_amount} {swap.from_asset}
                            </span>
                            <span className="text-gray-400">→</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {swap.to_amount?.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }) || '0.00'}{' '}
                              {swap.to_asset}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1 mt-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatTimeAgo(swap.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs font-medium rounded-full">
                        Completed
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Exchange;
